import React, { useState } from 'react';
import { Eye, EyeOff, User, ArrowLeft, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function Login({ onLogin }) {
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // two-step login: after the password, a code is sent to the person's Telegram
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Authentication failed');
      }

      if (data.two_factor_required) {
        setChallenge(data);
        setCode('');
        return;
      }
      onLogin(data.user, data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: challenge.challenge_id, code })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Code not accepted');
      onLogin(data.user, data.token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send reset link');
      }

      setSuccessMessage('If that account exists, a reset link has been sent to its email, or to Telegram for staff without email.');
      setForgotEmail('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <style>{`
        .login-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #0F172A 0%, #1E293B 100%);
          padding: 1rem;
        }
        .login-card {
          background: white;
          padding: 3rem;
          border-radius: 8px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          width: 100%;
          max-width: 450px;
        }
        .login-header {
          text-align: center;
          margin-bottom: 2rem;
        }
        .login-title {
          font-family: 'Chivo', sans-serif;
          font-size: 2rem;
          font-weight: 900;
          color: #0F172A;
          margin-bottom: 0.5rem;
        }
        .login-subtitle {
          color: #64748B;
          font-size: 0.95rem;
        }
        .password-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .password-input-wrapper input {
          padding-right: 45px;
        }
        .password-toggle-btn {
          position: absolute;
          right: 12px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #64748B;
          transition: color 0.2s;
        }
        .password-toggle-btn:hover {
          color: #0F172A;
        }
        .forgot-password-link {
          display: block;
          text-align: right;
          margin-top: 8px;
          margin-bottom: 16px;
          color: #3B82F6;
          font-size: 0.875rem;
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
          text-decoration: none;
        }
        .forgot-password-link:hover {
          color: #2563EB;
          text-decoration: underline;
        }
        .back-to-login {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #64748B;
          font-size: 0.875rem;
          cursor: pointer;
          background: none;
          border: none;
          padding: 0;
          margin-bottom: 1.5rem;
        }
        .back-to-login:hover {
          color: #0F172A;
        }
        .forgot-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #0F172A;
          margin-bottom: 0.5rem;
        }
        .forgot-subtitle {
          color: #64748B;
          font-size: 0.9rem;
          margin-bottom: 1.5rem;
        }
        .email-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }
        .email-input-wrapper input {
          padding-left: 45px;
        }
        .email-icon {
          position: absolute;
          left: 12px;
          color: #64748B;
          pointer-events: none;
        }
        .remember-link {
          text-align: center;
          margin-top: 1.5rem;
          color: #64748B;
          font-size: 0.875rem;
        }
        .remember-link button {
          color: #3B82F6;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          font-size: 0.875rem;
        }
        .remember-link button:hover {
          text-decoration: underline;
        }
      `}</style>

      <div className="login-card">
        {showForgotPassword ? (
          <>
            <button 
              type="button" 
              className="back-to-login" 
              onClick={() => {
                setShowForgotPassword(false);
                setError('');
                setSuccessMessage('');
                setForgotEmail('');
              }}
              data-testid="back-to-login-btn"
            >
              <ArrowLeft size={16} />
              Back to Login
            </button>
            
            <h2 className="forgot-title" data-testid="forgot-title">Forgot Password?</h2>
            <p className="forgot-subtitle">
              Enter your email, username or mobile number. We'll send a reset link to your email, or to
              your Telegram if you don't have email.
            </p>
            
            {error && (
              <div className="alert alert-error" data-testid="forgot-error">
                {error}
              </div>
            )}
            
            {successMessage && (
              <div className="alert alert-success" data-testid="forgot-success">
                {successMessage}
              </div>
            )}
            
            <form onSubmit={handleForgotPassword} data-testid="forgot-password-form">
              <div className="form-group">
                <label className="form-label">Email, username or mobile</label>
                <div className="email-input-wrapper">
                  <User size={18} className="email-icon" />
                  <input
                    type="text"
                    autoComplete="username"
                    autoCapitalize="none"
                    className="form-input"
                    data-testid="forgot-email-input"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              
              <button 
                type="submit" 
                className="btn btn-primary" 
                data-testid="send-reset-link-btn"
                style={{ width: '100%' }} 
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
            
            <div className="remember-link">
              Remember your password?{' '}
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setError('');
                  setSuccessMessage('');
                }}
                data-testid="login-link-btn"
              >
                Log in
              </button>
            </div>
          </>
        ) : challenge ? (
          <>
            <button type="button" className="back-to-login" onClick={() => { setChallenge(null); setError(''); }} data-testid="code-back-btn">
              <ArrowLeft size={16} />
              Back
            </button>
            <h2 className="forgot-title">Enter your login code</h2>
            <p className="forgot-subtitle" style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
              <Send size={18} style={{ flexShrink: 0, marginTop: 2, color: '#0088cc' }} />
              <span>We sent a 6-digit code to your <strong>Telegram</strong>. It expires in {challenge.expires_in_minutes || 5} minutes.</span>
            </p>
            {error && <div className="alert alert-error" data-testid="code-error">{error}</div>}
            <form onSubmit={handleVerifyCode} data-testid="code-form">
              <div className="form-group">
                <label className="form-label">Code</label>
                <input
                  className="form-input"
                  data-testid="code-input"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  style={{ fontSize: '1.4rem', letterSpacing: '0.4em', textAlign: 'center' }}
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary" data-testid="code-submit-btn" style={{ width: '100%' }} disabled={loading || code.length !== 6}>
                {loading ? 'Checking...' : 'Log in'}
              </button>
            </form>
            <p className="remember-link">No code? Check the PTC Fleet bot in Telegram, or go back and log in again for a new one.</p>
          </>
        ) : (
          <>
            <div className="login-header">
              <h1 className="login-title" data-testid="login-title">FLEET CONTROL</h1>
              <p className="login-subtitle">
                Sign in to your account
              </p>
            </div>

            {error && (
              <div className="alert alert-error" data-testid="error-message">
                {error}
              </div>
            )}
            
            {successMessage && (
              <div className="alert alert-success" data-testid="success-message">
                {successMessage}
              </div>
            )}

            <form onSubmit={handleSubmit} data-testid="login-form">
              <div className="form-group">
                <label className="form-label">Email, username or mobile</label>
                <input
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  className="form-input"
                  data-testid="email-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-input"
                    data-testid="password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    data-testid="password-toggle-btn"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
              
              <button
                type="button"
                className="forgot-password-link"
                onClick={() => {
                  setShowForgotPassword(true);
                  setError('');
                  setSuccessMessage('');
                }}
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </button>

              <button type="submit" className="btn btn-primary" data-testid="submit-btn" style={{ width: '100%' }} disabled={loading}>
                {loading ? 'Processing...' : 'Sign In'}
              </button>
            </form>

          </>
        )}
      </div>
    </div>
  );
}

export default Login;
